import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Pagination from "react-js-pagination";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import { Tooltip } from "@mui/material";
import { IoSearch } from "react-icons/io5";
import NotFound from "../../components/not-found/NotFound";
import IconButton from "../../components/Button/IconButton";
import { SecondaryButton } from "../../components/Button/Button";
import ModalBox from "../../components/modal/ModalBox";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import useDebounce from "../../hooks/useDebounce";
import findSerialNumber from "../../utils/findSerialNumber";
import { CapitalizeFirstWord } from "../../utils/CapitalizeFirstWord";
import { toastMessage } from "../../utils/toastMessage";
import profileImage from "../../Assests/image/profile_not_found.png";
import common from "../../routes/common";
import { consultant } from "../../routes/route";
import {
  fetchPerformanceAlertsApi,
  fetchLowVmpsConsultantIdsApi,
  savePerformanceSettingsApi,
} from "../../services/consultantPerformance";

const { NOT_FOUND } = common;

const flagText = {
  both: "Low online + High missed",
  high_missed: "High missed",
  low_online: "Low online time",
};

const formatPct = (value) =>
  value == null || value === "" ? "---" : `${Number(value) || 0}%`;
const formatSharePct = (value) =>
  value == null || value === "" || Number(value) <= 0 ? "---" : `${Number(value)}%`;
const formatMoney = (value) => `₹${Number(value || 0).toFixed(2)}`;

const instantTooltip = {
  enterDelay: 0,
  enterNextDelay: 0,
  leaveDelay: 0,
};

const HeadTip = ({ label, title }) => (
  <Tooltip {...instantTooltip} title={title}>
    <span style={{ cursor: "help", borderBottom: "1px dashed #999" }}>{label}</span>
  </Tooltip>
);

const CHANNEL_LABELS = {
  whatsapp: "WhatsApp",
  email: "Email",
  notification: "SMS / App Notification",
  notice: "Notice",
};

const DEFAULT_JOB_CHANNELS = {
  notice: true,
  email: false,
  whatsapp: true,
  notification: true,
};

const DEFAULT_JOB_CONTROL = {
  enabled: true,
  autoSend: true,
  sendToAll: false,
  channels: { ...DEFAULT_JOB_CHANNELS },
};

const jobChannelLabels = (job = {}) =>
  Object.entries(CHANNEL_LABELS)
    .filter(([key]) => job.channels?.[key] !== false)
    .map(([, label]) => label);

const checkboxStyle = {
  width: 20,
  height: 20,
  minWidth: 20,
  margin: 0,
  accentColor: "#D37676",
  cursor: "inherit",
};

const ToggleRow = ({ label, hint, checked, disabled, onChange }) => (
  <label
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 16,
      padding: "12px 14px",
      marginBottom: 8,
      border: "1px solid #e2e8f0",
      borderRadius: 8,
      background: disabled ? "#f8fafc" : "#fff",
      cursor: disabled ? "not-allowed" : "pointer",
    }}
  >
    <span style={{ flex: 1, minWidth: 0 }}>
      <strong style={{ display: "block", fontSize: 14, color: "#0f172a", lineHeight: 1.3 }}>{label}</strong>
      {hint ? <span style={{ display: "block", fontSize: 12, color: "#64748b", marginTop: 3, lineHeight: 1.4 }}>{hint}</span> : null}
    </span>
    <span
      style={{
        width: 28,
        height: 28,
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <input type="checkbox" checked={Boolean(checked)} disabled={disabled} onChange={onChange} style={checkboxStyle} />
    </span>
  </label>
);

const ChannelChip = ({ label, checked, disabled, onChange }) => (
  <label
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 14px",
      borderRadius: 999,
      border: checked && !disabled ? "1px solid #D37676" : "1px solid #e2e8f0",
      background: disabled ? "#f8fafc" : checked ? "#fff5f5" : "#fff",
      color: disabled ? "#94a3b8" : "#0f172a",
      cursor: disabled ? "not-allowed" : "pointer",
      fontSize: 13,
    }}
  >
    <input type="checkbox" checked={Boolean(checked)} disabled={disabled} onChange={onChange} style={checkboxStyle} />
    {label}
  </label>
);

const mergeOutreachState = (prev, next = {}) => ({
  ...prev,
  ...next,
  daily: {
    ...DEFAULT_JOB_CONTROL,
    ...prev.daily,
    ...next.daily,
    channels: {
      ...DEFAULT_JOB_CHANNELS,
      ...prev.daily?.channels,
      ...next.daily?.channels,
    },
  },
  weekly: {
    ...DEFAULT_JOB_CONTROL,
    ...prev.weekly,
    ...next.weekly,
    channels: {
      ...DEFAULT_JOB_CHANNELS,
      ...prev.weekly?.channels,
      ...next.weekly?.channels,
    },
  },
});

const COLUMN_TIPS = {
  sno: "Serial number on this page.",
  profile: "Consultant profile photo.",
  name: "Consultant display name. Opens the profile.",
  phone: "Registered phone number.",
  live: "Current live status from chat/call/video toggles.",
  chatTime: "Minutes the consultant was online on chat this day.",
  callTime: "Minutes the consultant was online on call this day.",
  bothOn: "Minutes both chat and call were on together this day.",
  totalTime: "Total online minutes this day / daily online goal hours.",
  missedChat: "Missed chat consultations this day.",
  missedCall: "Missed call consultations this day.",
  totalMissed: "Total missed consultations this day / missed limit.",
  sameRecharge:
    "Same as Dashboard Analytics 1st → 2nd recharge: users who completed that conversion (to count).",
  sameConsult:
    "Same as Dashboard Analytics 1st → 2nd consultation: users who completed that conversion (to count).",
  conversion:
    "Same as Dashboard Analytics 1st → 2nd consultation %. Users whose 1st paid consult was this day, then recharged, then took a 2nd paid consult with the same consultant.",
  sameDayRecharge:
    "Same as Dashboard Analytics 1st → 2nd recharge %. Sequence: 1st consult → 1st recharge (this day) → 2nd consult → 2nd recharge.",
  power:
    "Same as Dashboard Analytics Power / unique completed. Power customers are 6-month spend ≥ ₹1500 ending this date. Unique completed is this day's unique completed customers.",
  potential:
    "Same as Dashboard Analytics Power / potential power ratio. Power customers ÷ potential power customers (₹500–₹1499 in the same 6-month window).",
  vmps: "Weighted Vedic Meet Performance Score for this day (0–100).",
  select:
    "Tick consultants below the VMPS cutoff. Revenue share is applied only to selected consultants who are below cutoff.",
  revenueShare:
    "Percent of Actual Share for this date. Example: Actual 35% and Revenue 20% → consultant keeps 20% of that day's 35% earning (effective 7%). Empty means keep Actual Share.",
  actualShare:
    "Consultant's real split of the consultation (price.platformShare). Example: 35 means 35% consultant. Chat/Call/Fixed earning is already this share.",
  effectiveShare:
    "Actual Share × Revenue Share %. Example: 35% × 20% = 7% of total revenue. Empty when this consultant was not selected or VMPS is at/above cutoff.",
  totalRevenue:
    "Full money generated from this consultant's completed consultations this day, before company/consultant split. User pays ₹100 → Total Revenue ₹100. Consultant share is Chat/Call/Fixed earning.",
  chatEarning: "Same as Consultant Payout Chat earning: consult credits where mode is chat.",
  callEarning: "Same as Consultant Payout Call earning: consult/live credits where mode is call/audio/video.",
  fixedEarning: "Same as Consultant Payout Fixed session earning: session_book credits.",
  walletAdd: "Same as Consultant Payout Wallet addition: wallet_add credits this day. Wallet refund is added only in the PG/TDS ladder.",
  walletDeduct:
    "Same as Consultant Payout Wallet deduct: real non-payout debits this day. VMPS share is not deducted from wallet.",
  totalEarning:
    "This day's actual Chat + Call + Fixed earning (already the consultant's Actual Share). If Revenue Share is set on a selected low-VMPS consultant, they keep that percent of this earning.",
  pg: "PG% on payable after share (or on actual net if no share was set). Default 2.5%.",
  tds: "TDS% after PG, on payable after share. Default 10%.",
  afterCharges:
    "What they will get for this day after share + PG + TDS. Selected consultants below VMPS cutoff keep Revenue Share % of that day's earning. Wallet is not cut.",
  status: "Good if VMPS ≥ 70, Average if VMPS ≥ 40, otherwise Bad.",
  reason: "Why this consultant is flagged: low online time, high missed, or both.",
  action: "Open consultant details.",
};

const dateInIst = (offsetDays = 0) => {
  const now = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  now.setDate(now.getDate() + offsetDays);
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const ConsultantPerformanceAlerts = () => {
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [settings, setSettings] = useState({
    missedOpportunityLimit: 3,
    onlineGoalHours: 8,
    vmpsPayoutThreshold: 60,
    lowVmpsPayoutPercent: "",
  });
  const [revenueShareInput, setRevenueShareInput] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAllLowVmps, setSelectAllLowVmps] = useState(false);
  const [date, setDate] = useState(dateInIst(-1));
  const [search, setSearch] = useState("");
  const debounceValue = useDebounce(search || "", 300);
  const [reason, setReason] = useState("all");
  const [activePage, setActivePage] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState("alerts");
  const [outreach, setOutreach] = useState({
    daily: { ...DEFAULT_JOB_CONTROL, channels: { ...DEFAULT_JOB_CHANNELS } },
    weekly: { ...DEFAULT_JOB_CONTROL, channels: { ...DEFAULT_JOB_CHANNELS } },
    weeklyOnlineGoalHours: 56,
    weeklyMissedLimit: 21,
    meetingLink: "",
    meetingTime: "",
    meetingMode: "",
    meetingDate: "",
    dailyWhatsappCampaign: "",
    weeklyWhatsappCampaign: "",
  });
  const [confirmBox, setConfirmBox] = useState(null);
  const [result, setResult] = useState({
    list: [],
    total: 0,
    counts: { all: 0, lowOnline: 0, highMissed: 0, both: 0 },
  });

  const loadAlerts = useCallback(async () => {
    showLoader();
    const response = await fetchPerformanceAlertsApi({
      date,
      search: debounceValue,
      reason,
      page: activePage,
      limit: pageLimit,
    });
    hideLoader();
    if (response?.data?.success) {
      setResult(response.data.result);
      if (response.data.result?.settings) {
        const next = response.data.result.settings;
        setSettings(next);
        if (next.outreach) {
          setOutreach((prev) => mergeOutreachState(prev, next.outreach));
        }
        setRevenueShareInput(
          next.selectedDateShare != null && next.selectedDateShare !== ""
            ? String(next.selectedDateShare)
            : ""
        );
        setSelectedIds(Array.isArray(next.selectedDateConsultantIds) ? next.selectedDateConsultantIds.map(String) : []);
        setSelectAllLowVmps(Boolean(next.selectedDateApplyAll));
      }
    } else {
      toastMessage(response?.data?.message || "Could not load alerts", "error");
    }
  }, [activePage, date, debounceValue, hideLoader, pageLimit, reason, showLoader]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const isToday = date === dateInIst();
  const vmpsCutoff = Number(settings.vmpsPayoutThreshold) || 60;
  const isLowVmpsRow = (row) => !isToday && Number(row?.vmpsScore) < vmpsCutoff;
  const pageEligibleIds = (result.list || []).filter(isLowVmpsRow).map((row) => String(row._id));
  const allPageSelected =
    pageEligibleIds.length > 0 && pageEligibleIds.every((id) => selectedIds.includes(id));

  const toggleRow = (row) => {
    if (!isLowVmpsRow(row)) return;
    const key = String(row._id);
    setSelectAllLowVmps(false);
    setSelectedIds((prev) =>
      prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key]
    );
  };

  const togglePage = () => {
    setSelectAllLowVmps(false);
    setSelectedIds((prev) =>
      allPageSelected
        ? prev.filter((id) => !pageEligibleIds.includes(id))
        : Array.from(new Set([...prev, ...pageEligibleIds]))
    );
  };

  const requestSelectAllBelowVmps = () => {
    setConfirmBox({
      title: "Confirm Select All Below VMPS",
      intro: "Check what this will select. Revenue share is not saved until you click Save Criteria.",
      rows: [
        { label: "Date", value: date },
        { label: "VMPS Cutoff", value: String(vmpsCutoff) },
        { label: "Action", value: `Select every consultant below VMPS ${vmpsCutoff} on this date` },
      ],
      action: selectAllBelowVmps,
    });
  };

  const requestClearSelected = () => {
    setConfirmBox({
      title: "Confirm Clear Selected",
      intro: "Check what will be cleared. Saved revenue share does not change until you click Save Criteria.",
      rows: [
        { label: "Date", value: date },
        { label: "Currently selected", value: `${selectedIds.length} consultant(s)${selectAllLowVmps ? " (all below VMPS)" : ""}` },
        { label: "Action", value: "Unselect all consultants on this page selection" },
      ],
      action: () => {
        setSelectedIds([]);
        setSelectAllLowVmps(false);
        toastMessage("Selection cleared.");
      },
    });
  };

  const requestSaveSettings = () => {
    if (revenueShareInput !== "" && !selectAllLowVmps && !selectedIds.length) {
      toastMessage("Select consultants first, or use Select all below VMPS.", "error");
      return;
    }
    const shareText =
      revenueShareInput === ""
        ? "Empty — keep Actual Share (no cut)"
        : `${revenueShareInput}% of Actual Share`;
    const applyText =
      revenueShareInput === ""
        ? "Not applied (revenue share is empty)"
        : selectAllLowVmps
          ? `All consultants below VMPS ${vmpsCutoff} on this date`
          : `${selectedIds.length} selected consultant(s) below VMPS`;
    setConfirmBox({
      title: "Confirm Save Criteria",
      intro: "Check everything that will be saved for this date.",
      rows: [
        { label: "Date", value: date },
        { label: "Online Goal", value: `${settings.onlineGoalHours} hours` },
        { label: "Missed Limit", value: String(settings.missedOpportunityLimit) },
        { label: "VMPS Cutoff", value: String(settings.vmpsPayoutThreshold) },
        { label: "Revenue Share %", value: shareText },
        { label: "Apply revenue share to", value: applyText },
      ],
      note: "Recorded online time and missed counts are not edited. Only this date's goal, limit, cutoff, and revenue share are updated.",
      action: saveSettings,
    });
  };

  const requestSaveOutreach = () => {
    const dailyChannels = jobChannelLabels(outreach.daily);
    const weeklyChannels = jobChannelLabels(outreach.weekly);
    setConfirmBox({
      title: "Confirm Save Meeting & Outreach",
      intro: "Check daily 9 AM and weekly Monday cron settings separately.",
      rows: [
        { label: "Daily cron", value: outreach.daily?.enabled ? "On — 9:00 AM IST" : "Off — 9 AM messages will not go" },
        { label: "Daily send to", value: outreach.daily?.sendToAll ? "All consultants" : "Flagged consultants only" },
        { label: "Daily channels", value: dailyChannels.length ? dailyChannels.join(", ") : "None" },
        { label: "Daily WhatsApp campaign", value: outreach.dailyWhatsappCampaign || "Not set" },
        { label: "Weekly cron", value: outreach.weekly?.enabled ? "On — Monday 7:00 AM IST" : "Off — Monday messages will not go" },
        { label: "Weekly send to", value: outreach.weekly?.sendToAll ? "All consultants" : "Flagged consultants only" },
        { label: "Weekly channels", value: weeklyChannels.length ? weeklyChannels.join(", ") : "None" },
        { label: "Weekly WhatsApp campaign", value: outreach.weeklyWhatsappCampaign || "Not set" },
        { label: "Weekly Online Goal", value: `${outreach.weeklyOnlineGoalHours} hours` },
        { label: "Weekly Missed Limit", value: String(outreach.weeklyMissedLimit) },
        { label: "Meeting Date", value: outreach.meetingDate || "Not set" },
        { label: "Meeting Time", value: outreach.meetingTime || "Not set" },
        { label: "Meeting Mode", value: outreach.meetingMode || "Not set" },
        { label: "Meeting Link", value: outreach.meetingLink || "Not set" },
      ],
      action: saveOutreach,
    });
  };

  const handleConfirm = async () => {
    const fn = confirmBox?.action;
    setConfirmBox(null);
    if (fn) await fn();
  };

  const selectAllBelowVmps = async () => {
    showLoader();
    const response = await fetchLowVmpsConsultantIdsApi({ date });
    hideLoader();
    if (response?.data?.success) {
      const ids = (response.data.result?.consultantIds || []).map(String);
      setSelectedIds(ids);
      setSelectAllLowVmps(true);
      toastMessage(
        ids.length
          ? `Selected ${ids.length} consultants below VMPS ${vmpsCutoff}. Save Criteria to apply.`
          : "No consultants are below the VMPS cutoff for this date."
      );
    } else {
      toastMessage(response?.data?.message || "Could not load consultants below VMPS", "error");
    }
  };

  const saveSettings = async () => {
    showLoader();
    const response = await savePerformanceSettingsApi({
      date,
      missedOpportunityLimit: Number(settings.missedOpportunityLimit),
      onlineGoalHours: Number(settings.onlineGoalHours),
      vmpsPayoutThreshold: Number(settings.vmpsPayoutThreshold),
      lowVmpsPayoutPercent: revenueShareInput === "" ? "" : Number(revenueShareInput),
      shareApplyTo: selectAllLowVmps ? "allLowVmps" : "selected",
      consultantIds: selectedIds,
    });
    hideLoader();
    if (response?.data?.success) {
      toastMessage(
        revenueShareInput === ""
          ? "Criteria saved for the selected date. Online time itself is not changed; only that day's goal/limit is updated."
          : selectAllLowVmps
            ? "Revenue share will apply to all consultants below the VMPS cutoff on this date."
            : `Revenue share will apply to ${response.data.result?.selectedDateConsultantIds?.length || selectedIds.length} selected consultants below VMPS.`
      );
      const next = response.data.result;
      setSettings(next);
      setRevenueShareInput(
        next.selectedDateShare != null && next.selectedDateShare !== ""
          ? String(next.selectedDateShare)
          : ""
      );
      setSelectedIds(Array.isArray(next.selectedDateConsultantIds) ? next.selectedDateConsultantIds.map(String) : []);
      setSelectAllLowVmps(Boolean(next.selectedDateApplyAll));
      setActivePage(1);
      loadAlerts();
    } else {
      toastMessage(response?.data?.message || "Could not save settings", "error");
    }
  };

  const saveOutreach = async () => {
    showLoader();
    try {
      const response = await savePerformanceSettingsApi({ outreach });
      if (response?.data?.success) {
        toastMessage(
          [
            outreach.daily?.enabled ? "Daily 9 AM cron is on." : "Daily 9 AM cron is off.",
            outreach.weekly?.enabled ? "Weekly Monday 7 AM cron is on." : "Weekly Monday 7 AM cron is off.",
          ].join(" ")
        );
        if (response.data.result?.outreach) {
          setOutreach((prev) => mergeOutreachState(prev, response.data.result.outreach));
        }
      } else {
        toastMessage(response?.data?.message || "Could not save outreach settings", "error");
      }
    } catch (error) {
      toastMessage(error?.response?.data?.message || error.message || "Could not save outreach settings", "error");
    } finally {
      hideLoader();
    }
  };

  const setCronOn = (type, on) => {
    patchJob(type, { enabled: on, autoSend: on });
  };

  const patchJob = (type, patch) => {
    setOutreach((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        ...patch,
        channels: patch.channels
          ? { ...prev[type]?.channels, ...patch.channels }
          : prev[type]?.channels,
      },
    }));
  };

  const toggleJobChannel = (type, key) => {
    setOutreach((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        channels: {
          ...prev[type]?.channels,
          [key]: !prev[type]?.channels?.[key],
        },
      },
    }));
  };

  const refreshHandler = () => {
    setSearch("");
    setReason("all");
    setDate(dateInIst(-1));
    setActivePage(1);
  };

  return (
    <>
      <div className="WrapperArea">
        <div className="WrapperBox">
          {loader}
          <div className="TitleBox">
            <Tooltip
              {...instantTooltip}
              title="Saved values apply to this list and the consultant app. Previous day shows consultants below the online goal or at/above the missed limit. Today shows missed limit only."
            >
              <h4 className="Title">Performance Alerts</h4>
            </Tooltip>
            <h5>
              Flagged: <strong>{result.counts?.all || 0}</strong>
              {date === dateInIst() ? (
                <>
                  {"  "}Missed: <strong>{result.counts?.highMissed || 0}</strong>
                </>
              ) : (
                <>
                  {"  "}Low online: <strong>{result.counts?.lowOnline || 0}</strong>
                  {"  "}High missed: <strong>{result.counts?.highMissed || 0}</strong>
                  {"  "}Both: <strong>{result.counts?.both || 0}</strong>
                </>
              )}
            </h5>
          </div>

          <div className="FilterBox FilterArea" style={{ gap: 8, marginBottom: 12 }}>
            <SecondaryButton
              onClick={() => setActiveTab("alerts")}
              className={activeTab === "alerts" ? "active" : ""}
            >
              Alerts
            </SecondaryButton>
            <SecondaryButton
              onClick={() => setActiveTab("outreach")}
              className={activeTab === "outreach" ? "active" : ""}
            >
              Meeting & Outreach
            </SecondaryButton>
          </div>

          {activeTab === "outreach" ? (
            <>
              <p style={{ margin: "0 0 16px", color: "#475569", fontSize: 13 }}>
                Sirf cron on/off. Daily 9:00 AM aur Weekly Monday 7:00 AM alag hain. Off ho to us time pe kisi ko message nahi jayega.
              </p>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "stretch", marginBottom: 16 }}>
                <div
                  style={{
                    flex: "1 1 420px",
                    minWidth: 320,
                    border: outreach.daily?.enabled ? "1px solid #dbe3ef" : "1px solid #f1b0b0",
                    background: outreach.daily?.enabled ? "#fff" : "#fff8f8",
                    borderRadius: 12,
                    padding: 16,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div>
                      <h5 style={{ margin: 0 }}>Daily — 9:00 AM IST</h5>
                      <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 12 }}>
                        Previous day missed limit or below online goal
                      </p>
                    </div>
                    <span
                      style={{
                        background: outreach.daily?.enabled ? "#dcfce7" : "#fee2e2",
                        color: outreach.daily?.enabled ? "#166534" : "#991b1b",
                        padding: "4px 10px",
                        borderRadius: 999,
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {outreach.daily?.enabled ? "ON" : "OFF"}
                    </span>
                  </div>
                  <ToggleRow
                    label="Cron"
                    hint="On = har din 9:00 AM IST pe automatic. Off = 9 AM pe kisi ko nahi jayega."
                    checked={outreach.daily?.enabled !== false}
                    onChange={(event) => setCronOn("daily", event.target.checked)}
                  />
                  <ToggleRow
                    label="Send to all consultants"
                    hint={outreach.daily?.sendToAll ? "All active verified consultants" : "Only flagged consultants"}
                    checked={outreach.daily?.sendToAll === true}
                    onChange={(event) => patchJob("daily", { sendToAll: event.target.checked })}
                  />
                  <p style={{ margin: "12px 0 8px", fontSize: 13, fontWeight: 600 }}>What to send at 9 AM</p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                    {Object.entries(CHANNEL_LABELS).map(([key, label]) => (
                      <ChannelChip
                        key={`daily-${key}`}
                        label={label}
                        checked={outreach.daily?.channels?.[key] !== false}
                        onChange={() => toggleJobChannel("daily", key)}
                      />
                    ))}
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Daily WhatsApp Campaign</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="AiSensy campaign name"
                      value={outreach.dailyWhatsappCampaign}
                      onChange={(event) =>
                        setOutreach((prev) => ({ ...prev, dailyWhatsappCampaign: event.target.value }))
                      }
                    />
                  </div>
                </div>

                <div
                  style={{
                    flex: "1 1 420px",
                    minWidth: 320,
                    border: outreach.weekly?.enabled ? "1px solid #dbe3ef" : "1px solid #f1b0b0",
                    background: outreach.weekly?.enabled ? "#fff" : "#fff8f8",
                    borderRadius: 12,
                    padding: 16,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div>
                      <h5 style={{ margin: 0 }}>Weekly — Monday 7:00 AM IST</h5>
                      <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 12 }}>
                        Previous Mon–Sun online goal or missed limit
                      </p>
                    </div>
                    <span
                      style={{
                        background: outreach.weekly?.enabled ? "#dcfce7" : "#fee2e2",
                        color: outreach.weekly?.enabled ? "#166534" : "#991b1b",
                        padding: "4px 10px",
                        borderRadius: 999,
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {outreach.weekly?.enabled ? "ON" : "OFF"}
                    </span>
                  </div>
                  <ToggleRow
                    label="Cron"
                    hint="On = har Monday 7:00 AM IST pe automatic. Off = Monday pe kisi ko nahi jayega."
                    checked={outreach.weekly?.enabled !== false}
                    onChange={(event) => setCronOn("weekly", event.target.checked)}
                  />
                  <ToggleRow
                    label="Send to all consultants"
                    hint={outreach.weekly?.sendToAll ? "All active verified consultants" : "Only flagged consultants"}
                    checked={outreach.weekly?.sendToAll === true}
                    onChange={(event) => patchJob("weekly", { sendToAll: event.target.checked })}
                  />
                  <p style={{ margin: "12px 0 8px", fontSize: 13, fontWeight: 600 }}>What to send on Monday</p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                    {Object.entries(CHANNEL_LABELS).map(([key, label]) => (
                      <ChannelChip
                        key={`weekly-${key}`}
                        label={label}
                        checked={outreach.weekly?.channels?.[key] !== false}
                        onChange={() => toggleJobChannel("weekly", key)}
                      />
                    ))}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 8 }}>
                    <div className="form-group" style={{ flex: "1 1 140px", minWidth: 140 }}>
                      <label>Weekly Online Goal</label>
                      <input
                        type="number"
                        min={1}
                        className="form-control"
                        value={outreach.weeklyOnlineGoalHours}
                        onChange={(event) =>
                          setOutreach((prev) => ({ ...prev, weeklyOnlineGoalHours: event.target.value }))
                        }
                      />
                    </div>
                    <div className="form-group" style={{ flex: "1 1 140px", minWidth: 140 }}>
                      <label>Weekly Missed Limit</label>
                      <input
                        type="number"
                        min={1}
                        className="form-control"
                        value={outreach.weeklyMissedLimit}
                        onChange={(event) =>
                          setOutreach((prev) => ({ ...prev, weeklyMissedLimit: event.target.value }))
                        }
                      />
                    </div>
                    <div className="form-group" style={{ flex: "1 1 140px", minWidth: 140 }}>
                      <label>Meeting Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={outreach.meetingDate}
                        onChange={(event) =>
                          setOutreach((prev) => ({ ...prev, meetingDate: event.target.value }))
                        }
                      />
                    </div>
                    <div className="form-group" style={{ flex: "1 1 140px", minWidth: 140 }}>
                      <label>Meeting Time</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="11:00 AM"
                        value={outreach.meetingTime}
                        onChange={(event) =>
                          setOutreach((prev) => ({ ...prev, meetingTime: event.target.value }))
                        }
                      />
                    </div>
                    <div className="form-group" style={{ flex: "1 1 140px", minWidth: 140 }}>
                      <label>Meeting Mode</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Google Meet / Zoom"
                        value={outreach.meetingMode}
                        onChange={(event) =>
                          setOutreach((prev) => ({ ...prev, meetingMode: event.target.value }))
                        }
                      />
                    </div>
                    <div className="form-group" style={{ flex: "1 1 220px", minWidth: 180 }}>
                      <label>Meeting Link</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="https://meet.google.com/..."
                        value={outreach.meetingLink}
                        onChange={(event) =>
                          setOutreach((prev) => ({ ...prev, meetingLink: event.target.value }))
                        }
                      />
                    </div>
                    <div className="form-group" style={{ flex: "1 1 220px", minWidth: 180 }}>
                      <label>Weekly WhatsApp Campaign</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="AiSensy campaign name"
                        value={outreach.weeklyWhatsappCampaign}
                        onChange={(event) =>
                          setOutreach((prev) => ({ ...prev, weeklyWhatsappCampaign: event.target.value }))
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="FilterBox FilterArea" style={{ marginBottom: 8 }}>
                <div className="form-group refresh_button">
                  <SecondaryButton onClick={requestSaveOutreach}>Save Meeting & Outreach</SecondaryButton>
                </div>
              </div>
              <p style={{ color: "#64748b", fontSize: 12, marginBottom: 0 }}>
                Notice shows on consultant app Home → Important Notices. WhatsApp needs the AiSensy campaign name.
                SMS / App Notification is the consultant app push. Save ke baad hi cron nayi setting use karega.
              </p>
            </>
          ) : null}

          {activeTab === "alerts" ? (
          <>
          <div className="FilterBox FilterArea" style={{ alignItems: "flex-end" }}>
            <div className="form-group" style={{ minWidth: 170, flex: "0 0 170px" }}>
              <Tooltip
                {...instantTooltip}
                title="Goal hours for the selected date only. Recorded online time cannot be edited. Save 6 on 26 Sep to flag that day against 6 hours; other dates stay on their own goal."
              >
                <label htmlFor="online-goal-hours" style={{ whiteSpace: "nowrap" }}>
                  Online Goal
                </label>
              </Tooltip>
              <input
                id="online-goal-hours"
                type="number"
                min={1}
                className="form-control"
                value={settings.onlineGoalHours}
                onChange={(event) =>
                  setSettings((prev) => ({
                    ...prev,
                    onlineGoalHours: event.target.value,
                  }))
                }
              />
            </div>
            <div className="form-group" style={{ minWidth: 170, flex: "0 0 170px" }}>
              <Tooltip
                {...instantTooltip}
                title="Missed limit for the selected date only. Recorded missed count cannot be edited. Save updates only this date's flagging."
              >
                <label htmlFor="missed-limit" style={{ whiteSpace: "nowrap" }}>
                  Missed Limit
                </label>
              </Tooltip>
              <input
                id="missed-limit"
                type="number"
                min={1}
                className="form-control"
                value={settings.missedOpportunityLimit}
                onChange={(event) =>
                  setSettings((prev) => ({
                    ...prev,
                    missedOpportunityLimit: event.target.value,
                  }))
                }
              />
            </div>
            <div className="form-group" style={{ minWidth: 170, flex: "0 0 170px" }}>
              <Tooltip
                {...instantTooltip}
                title="VMPS cutoff for the selected date. Revenue share is applied only to selected consultants whose VMPS is below this cutoff."
              >
                <label htmlFor="vmps-threshold" style={{ whiteSpace: "nowrap" }}>
                  VMPS Cutoff
                </label>
              </Tooltip>
              <input
                id="vmps-threshold"
                type="number"
                min={1}
                className="form-control"
                value={settings.vmpsPayoutThreshold}
                onChange={(event) =>
                  setSettings((prev) => ({
                    ...prev,
                    vmpsPayoutThreshold: event.target.value,
                  }))
                }
              />
            </div>
            <div className="form-group" style={{ minWidth: 170, flex: "0 0 170px" }}>
              <Tooltip
                {...instantTooltip}
                title="Percent of Actual Share, not a replacement share. Actual 35% and 20% here means they keep 20% of that day's 35% earning (effective 7%). Leave empty to keep Actual Share."
              >
                <label htmlFor="low-vmps-share" style={{ whiteSpace: "nowrap" }}>
                  Revenue Share %
                </label>
              </Tooltip>
              <input
                id="low-vmps-share"
                type="number"
                min={1}
                max={100}
                className="form-control"
                placeholder="Empty = actual share"
                value={revenueShareInput}
                onChange={(event) => setRevenueShareInput(event.target.value)}
              />
            </div>
            <div className="form-group refresh_button">
              <SecondaryButton onClick={requestSelectAllBelowVmps} disabled={isToday}>
                Select all below VMPS
              </SecondaryButton>
            </div>
            <div className="form-group refresh_button">
              <SecondaryButton
                onClick={requestClearSelected}
                disabled={!selectedIds.length}
              >
                Clear selected
              </SecondaryButton>
            </div>
            <div className="form-group refresh_button">
              <SecondaryButton onClick={requestSaveSettings}>Save Criteria</SecondaryButton>
            </div>
          </div>
          <p style={{ margin: "0 0 12px", color: "#475569", fontSize: 13 }}>
            Revenue share = percent of Actual Share. Example: Actual 35%, Revenue 20% → keep ₹ of that day's earning × 20% (effective 7%).
            Apply with checkboxes or Select all below VMPS. Only consultants below cutoff {vmpsCutoff} are cut.
            {selectedIds.length ? ` ${selectedIds.length} selected${selectAllLowVmps ? " (all below VMPS)" : ""}.` : ""}
          </p>
          {result.settings?.selectedDateHistory?.length ||
          result.settings?.selectedDateCriteriaHistory?.length ? (
            <div className="content-table" style={{ marginTop: 8, marginBottom: 12 }}>
              {result.settings?.selectedDateCriteriaHistory?.length ? (
                <table>
                  <thead>
                    <tr>
                      <th>Criteria History ({date})</th>
                      <th>Admin ID</th>
                      <th>Admin</th>
                      <th>Online Goal</th>
                      <th>Missed Limit</th>
                      <th>VMPS Cutoff</th>
                      <th>Updated At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.settings.selectedDateCriteriaHistory.map((item, index) => (
                      <tr key={`criteria-${item.adminId}-${item.createdAt}-${index}`}>
                        <td>Online goal / missed / VMPS updated</td>
                        <td>{item.adminId || "---"}</td>
                        <td>{item.adminName || "---"}</td>
                        <td>{item.onlineGoalHours} hr</td>
                        <td>{item.missedOpportunityLimit}</td>
                        <td>{item.vmpsPayoutThreshold}</td>
                        <td>
                          {item.createdAt
                            ? new Date(item.createdAt).toLocaleString("en-IN", {
                                timeZone: "Asia/Kolkata",
                              })
                            : "---"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
              {result.settings?.selectedDateHistory?.length ? (
                <table>
                  <thead>
                    <tr>
                      <th>Revenue Share History ({date})</th>
                      <th>Admin ID</th>
                      <th>Admin</th>
                      <th>Share</th>
                      <th>Consultants</th>
                      <th>Updated At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.settings.selectedDateHistory.map((item, index) => (
                      <tr key={`${item.adminId}-${item.createdAt}-${index}`}>
                        <td>Revenue share updated</td>
                        <td>{item.adminId || "---"}</td>
                        <td>{item.adminName || "---"}</td>
                        <td>{formatPct(item.share)}</td>
                        <td>
                          {item.applyAll
                            ? `All below VMPS (${item.consultantCount || 0})`
                            : item.consultantCount || "---"}
                        </td>
                        <td>
                          {item.createdAt
                            ? new Date(item.createdAt).toLocaleString("en-IN", {
                                timeZone: "Asia/Kolkata",
                              })
                            : "---"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
            </div>
          ) : null}

          <div className="FilterBox FilterArea">
            <div className="form-group date_picker">
              <input
                id="alert-date"
                type="date"
                className={`form-control ${date && "active"}`}
                value={date}
                onChange={(event) => {
                  setActivePage(1);
                  setDate(event.target.value);
                  setSelectedIds([]);
                  setSelectAllLowVmps(false);
                  if (event.target.value === dateInIst() && reason !== "high_missed") {
                    setReason("all");
                  }
                }}
              />
            </div>
            <div className="form-group search_lg_size userSearch">
              <input
                type="search"
                className={`form-control ${search && "active"}`}
                placeholder="Search by Name, Phone, ID"
                value={search || ""}
                onChange={(event) => {
                  setActivePage(1);
                  setSearch(event.target.value);
                }}
                style={{ "--color": "#FF8989" }}
              />
              <span className="user_search_icon">
                <IoSearch />
              </span>
            </div>
            <div className="form-group select_sm_width">
              <select
                className={`form-control ${reason !== "all" && "active"}`}
                value={reason}
                onChange={(event) => {
                  setActivePage(1);
                  setReason(event.target.value);
                }}
              >
                <option value="all">All Flagged</option>
                {date !== dateInIst() ? (
                  <>
                    <option value="low_online">Low Online Time</option>
                    <option value="both">Both</option>
                  </>
                ) : null}
                <option value="high_missed">High Missed</option>
              </select>
            </div>
            <div className="form-group refresh_button">
              <SecondaryButton onClick={refreshHandler}>
                <RotateLeftIcon />
              </SecondaryButton>
            </div>
          </div>

          <div className="content-table">
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      disabled={!pageEligibleIds.length}
                      onChange={togglePage}
                      title="Select all below VMPS on this page"
                    />
                  </th>
                  <th><HeadTip label="S. No." title={COLUMN_TIPS.sno} /></th>
                  <th><HeadTip label="Profile" title={COLUMN_TIPS.profile} /></th>
                  <th><HeadTip label="Name" title={COLUMN_TIPS.name} /></th>
                  <th><HeadTip label="Phone Number" title={COLUMN_TIPS.phone} /></th>
                  <th><HeadTip label="Live Status" title={COLUMN_TIPS.live} /></th>
                  <th><HeadTip label="Chat Time" title={COLUMN_TIPS.chatTime} /></th>
                  <th><HeadTip label="Call Time" title={COLUMN_TIPS.callTime} /></th>
                  <th><HeadTip label="Both ON" title={COLUMN_TIPS.bothOn} /></th>
                  <th><HeadTip label="Total Time" title={COLUMN_TIPS.totalTime} /></th>
                  <th><HeadTip label="Missed Chat" title={COLUMN_TIPS.missedChat} /></th>
                  <th><HeadTip label="Missed Call" title={COLUMN_TIPS.missedCall} /></th>
                  <th><HeadTip label="Total Missed" title={COLUMN_TIPS.totalMissed} /></th>
                  <th><HeadTip label="1st → 2nd Recharge" title={COLUMN_TIPS.sameRecharge} /></th>
                  <th><HeadTip label="1st → 2nd Consultation" title={COLUMN_TIPS.sameConsult} /></th>
                  <th><HeadTip label="1st → 2nd Consult %" title={COLUMN_TIPS.conversion} /></th>
                  <th><HeadTip label="1st → 2nd Recharge %" title={COLUMN_TIPS.sameDayRecharge} /></th>
                  <th><HeadTip label="Power / Unique Completed" title={COLUMN_TIPS.power} /></th>
                  <th><HeadTip label="Power / Potential Power" title={COLUMN_TIPS.potential} /></th>
                  <th><HeadTip label="VMPS" title={COLUMN_TIPS.vmps} /></th>
                  <th><HeadTip label="Revenue Share %" title={COLUMN_TIPS.revenueShare} /></th>
                  <th><HeadTip label="Actual Share %" title={COLUMN_TIPS.actualShare} /></th>
                  <th><HeadTip label="Effective Share %" title={COLUMN_TIPS.effectiveShare} /></th>
                  <th><HeadTip label="Total Revenue" title={COLUMN_TIPS.totalRevenue} /></th>
                  <th><HeadTip label="Chat earning" title={COLUMN_TIPS.chatEarning} /></th>
                  <th><HeadTip label="Call earning" title={COLUMN_TIPS.callEarning} /></th>
                  <th><HeadTip label="Fixed session earning" title={COLUMN_TIPS.fixedEarning} /></th>
                  <th><HeadTip label="Wallet Add" title={COLUMN_TIPS.walletAdd} /></th>
                  <th><HeadTip label="Wallet Deduct" title={COLUMN_TIPS.walletDeduct} /></th>
                  <th><HeadTip label="Total Earning" title={COLUMN_TIPS.totalEarning} /></th>
                  <th><HeadTip label="PG (2.5%)" title={COLUMN_TIPS.pg} /></th>
                  <th><HeadTip label="TDS (10%)" title={COLUMN_TIPS.tds} /></th>
                  <th><HeadTip label="Final payable" title={COLUMN_TIPS.afterCharges} /></th>
                  <th><HeadTip label="Performance Status" title={COLUMN_TIPS.status} /></th>
                  <th><HeadTip label="Reason" title={COLUMN_TIPS.reason} /></th>
                  <th><HeadTip label="Action" title={COLUMN_TIPS.action} /></th>
                </tr>
              </thead>
              <tbody>
                {result.list?.length > 0 ? (
                  result.list.map((row, index) => (
                    <tr key={row._id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(String(row._id))}
                          disabled={!isLowVmpsRow(row)}
                          onChange={() => toggleRow(row)}
                          title={
                            isLowVmpsRow(row)
                              ? "Apply VMPS revenue share to this consultant"
                              : "Only consultants below the VMPS cutoff can be selected"
                          }
                        />
                      </td>
                      <td>{findSerialNumber(index, activePage, pageLimit)}</td>
                      <td className="user_profile_image">
                        <img
                          src={row.profileImage || profileImage}
                          alt={`${row.name} profile`}
                          onError={(event) => {
                            event.currentTarget.src = profileImage;
                          }}
                        />
                      </td>
                      <td>
                        <Link to={`${consultant.CONSULTANT_DETAIL}/${row._id}?from=management&alertDate=${date}`}>
                          {row.name ? CapitalizeFirstWord(row.name) : "---"}
                        </Link>
                      </td>
                      <td>
                        {row.phonePrefix ? `+${row.phonePrefix}` : ""}
                        {row.phone || "---"}
                      </td>
                      <td>{row.isOnline ? "Online" : "Offline"}</td>
                      <td>{row.chatOnlineLabel || "---"}</td>
                      <td>{row.callOnlineLabel || "---"}</td>
                      <td>{row.bothOnlineLabel || "---"}</td>
                      <td>
                        {row.onlineLabel || "---"} / {row.onlineGoalHours} hr
                      </td>
                      <td>{row.missedChat ?? 0}</td>
                      <td>{row.missedCall ?? 0}</td>
                      <td>
                        {row.missedTotal ?? 0} / {row.missedLimit}
                      </td>
                      <td>{row.sameCustomerRecharge ?? 0} / {row.recharge1to2From ?? 0}</td>
                      <td>{row.sameCustomerConsultation ?? 0} / {row.consult1to2From ?? 0}</td>
                      <td>{formatPct(row.conversionPercentage)}</td>
                      <td>{formatPct(row.sameDayRechargeConversion)}</td>
                      <td>{formatPct(row.powerConversion)}</td>
                      <td>{formatPct(row.potentialToPowerConversion)}</td>
                      <td>{row.vmpsScore ?? 0}</td>
                      <td>{formatSharePct(row.revenueShare)}</td>
                      <td>{formatSharePct(row.actualShare)}</td>
                      <td>{formatSharePct(row.effectiveShare)}</td>
                      <td>{formatMoney(row.totalRevenue)}</td>
                      <td>{formatMoney(row.chatEarning)}</td>
                      <td>{formatMoney(row.callEarning)}</td>
                      <td>{formatMoney(row.fixedEarning)}</td>
                      <td>{formatMoney(row.walletAdd)}</td>
                      <td>{formatMoney(row.walletDeduct)}</td>
                      <td>{formatMoney(row.totalEarning)}</td>
                      <td>{formatMoney(row.paymentGatewayCharge)}</td>
                      <td>{formatMoney(row.tdsCharge)}</td>
                      <td>{formatMoney(row.finalPayable ?? row.earningAfterCharges)}</td>
                      <td>{row.performanceStatus || "---"}</td>
                      <td>{flagText[row.flag] || row.flag || "---"}</td>
                      <td>
                        <div className="action_button">
                          <IconButton
                            aria-label="details"
                            className="px-2 py-0"
                            onClick={() => setSelected(row)}
                          >
                            <VisibilityIcon className="eye_icon" />
                          </IconButton>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <NotFound
                    cols={36}
                    msg="Consultant not found!"
                    icon={NOT_FOUND}
                    loader={null}
                  />
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination-wrapper mt-4 d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <span>Show:</span>
              <select
                value={pageLimit}
                onChange={(event) => {
                  setPageLimit(Number(event.target.value));
                  setActivePage(1);
                }}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px",
                  minWidth: "80px",
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span>per page</span>
            </div>
            <span>
              Showing {result.total ? (activePage - 1) * pageLimit + 1 : 0} to{" "}
              {Math.min(activePage * pageLimit, result.total || 0)} of {result.total || 0} items
            </span>
            {result.total > pageLimit ? (
              <div className="pagination-wrapper">
                <div className="Pagination">
                  <Pagination
                    activePage={activePage}
                    previousLabel="previous"
                    nextLabel="next"
                    itemsCountPerPage={pageLimit}
                    totalItemsCount={result.total}
                    pageRangeDisplayed={5}
                    onChange={setActivePage}
                    hideDisabled={true}
                  />
                </div>
              </div>
            ) : null}
          </div>
          </>
          ) : null}
        </div>
      </div>

      {selected ? (
        <ModalBox
          status={Boolean(selected)}
          onClose={() => setSelected(null)}
          title="Consultant Details"
          closeIcon
          noFooter
          size="md"
        >
          <div className="content-table">
            <table>
              <tbody>
                <tr>
                  <th>Name</th>
                  <td>{selected.name || "---"}</td>
                </tr>
                <tr>
                  <th>Phone</th>
                  <td>
                    {selected.phonePrefix ? `+${selected.phonePrefix}` : ""}
                    {selected.phone || "---"}
                  </td>
                </tr>
                <tr>
                  <th>Email</th>
                  <td>{selected.email || "---"}</td>
                </tr>
                <tr>
                  <th>Live Status</th>
                  <td>
                    Chat: {selected.liveModes?.chat ? "On" : "Off"}, Call:{" "}
                    {selected.liveModes?.call ? "On" : "Off"}, Video:{" "}
                    {selected.liveModes?.video ? "On" : "Off"}
                  </td>
                </tr>
                <tr>
                  <th>Online Time</th>
                  <td>
                    Total {selected.onlineLabel} / {selected.onlineGoalHours} hr
                    <br />
                    Chat {selected.chatOnlineLabel}, Call {selected.callOnlineLabel}, Both{" "}
                    {selected.bothOnlineLabel}
                  </td>
                </tr>
                <tr>
                  <th>Missed</th>
                  <td>
                    Total {selected.missedTotal} / {selected.missedLimit}
                    <br />
                    Chat {selected.missedChat}, Call {selected.missedCall}
                  </td>
                </tr>
                <tr>
                  <th>1st → 2nd Recharge</th>
                  <td>{selected.sameCustomerRecharge ?? 0} / {selected.recharge1to2From ?? 0}</td>
                </tr>
                <tr>
                  <th>1st → 2nd Consultation</th>
                  <td>{selected.sameCustomerConsultation ?? 0} / {selected.consult1to2From ?? 0}</td>
                </tr>
                <tr>
                  <th>1st → 2nd Consult %</th>
                  <td>{formatPct(selected.conversionPercentage)}</td>
                </tr>
                <tr>
                  <th>1st → 2nd Recharge %</th>
                  <td>{formatPct(selected.sameDayRechargeConversion)}</td>
                </tr>
                <tr>
                  <th>Power / Unique Completed</th>
                  <td>{formatPct(selected.powerConversion)}</td>
                </tr>
                <tr>
                  <th>Power / Potential Power</th>
                  <td>{formatPct(selected.potentialToPowerConversion)}</td>
                </tr>
                <tr>
                  <th>VMPS</th>
                  <td>{selected.vmpsScore ?? 0}</td>
                </tr>
                <tr>
                  <th>Revenue Share %</th>
                  <td>{formatSharePct(selected.revenueShare)}</td>
                </tr>
                <tr>
                  <th>Actual Share %</th>
                  <td>{formatSharePct(selected.actualShare)}</td>
                </tr>
                <tr>
                  <th>Effective Share %</th>
                  <td>{formatSharePct(selected.effectiveShare)}</td>
                </tr>
                <tr>
                  <th>Total Revenue</th>
                  <td>{formatMoney(selected.totalRevenue)}</td>
                </tr>
                <tr>
                  <th>Chat earning</th>
                  <td>{formatMoney(selected.chatEarning)}</td>
                </tr>
                <tr>
                  <th>Call earning</th>
                  <td>{formatMoney(selected.callEarning)}</td>
                </tr>
                <tr>
                  <th>Fixed session earning</th>
                  <td>{formatMoney(selected.fixedEarning)}</td>
                </tr>
                <tr>
                  <th>Wallet Add</th>
                  <td>{formatMoney(selected.walletAdd)}</td>
                </tr>
                <tr>
                  <th>Wallet Deduct</th>
                  <td>{formatMoney(selected.walletDeduct)}</td>
                </tr>
                <tr>
                  <th>Total Earning</th>
                  <td>{formatMoney(selected.totalEarning)}</td>
                </tr>
                <tr>
                  <th>PG (2.5%)</th>
                  <td>{formatMoney(selected.paymentGatewayCharge)}</td>
                </tr>
                <tr>
                  <th>TDS (10%)</th>
                  <td>{formatMoney(selected.tdsCharge)}</td>
                </tr>
                <tr>
                  <th>Final payable</th>
                  <td>{formatMoney(selected.finalPayable ?? selected.earningAfterCharges)}</td>
                </tr>
                <tr>
                  <th>Performance Status</th>
                  <td>{selected.performanceStatus || "---"}</td>
                </tr>
                <tr>
                  <th>Reason</th>
                  <td>{flagText[selected.flag] || selected.flag}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-3">
            <Link to={`${consultant.CONSULTANT_DETAIL}/${selected._id}?from=management&alertDate=${date}`} className="me-3">
              Open Profile
            </Link>
            <Link
              to={`${consultant.CONSULTANT_CHECK_PERFORMANCE}/${selected._id}/${encodeURIComponent(
                selected.name || "Consultant"
              )}`}
            >
              Check Performance
            </Link>
          </div>
        </ModalBox>
      ) : null}

      {confirmBox ? (
        <ModalBox
          status={Boolean(confirmBox)}
          onClose={() => setConfirmBox(null)}
          title={confirmBox.title}
          closeIcon
          action={handleConfirm}
        >
          {confirmBox.intro ? (
            <p style={{ marginBottom: 12, color: "#334155" }}>{confirmBox.intro}</p>
          ) : null}
          <div className="content-table">
            <table>
              <tbody>
                {(confirmBox.rows || []).map((row) => (
                  <tr key={row.label}>
                    <th style={{ width: "40%" }}>{row.label}</th>
                    <td>{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {confirmBox.note ? (
            <p style={{ marginTop: 12, color: "#64748b", fontSize: 13 }}>{confirmBox.note}</p>
          ) : null}
        </ModalBox>
      ) : null}
    </>
  );
};

export default ConsultantPerformanceAlerts;
